import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Link,
	Snackbar,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { alpha } from '@mui/system';
import { get } from 'lodash';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import backgroundImage from '../../../assets/images/login/background-image.png';
import logoLinks from '../../../assets/images/logo/Logo_Links.svg';
import logoRecht from '../../../assets/images/logo/Logo_Recht.svg';
import { useAuthentication } from '../../../context/AuthContext';
import { PAGES_URLS } from '../../../lib/constants/routes';

function Login() {
	const navigate = useNavigate();
	const { login } = useAuthentication();

	const [identifier, setIdentifier] = useState<string>('');
	const [password, setPassword] = useState<string>('');
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [errMsg, setErrMsg] = useState('');
	const [openRegistrationDialog, setOpenRegistrationDialog] = useState(false);

	const handleLogin = async (event: React.FormEvent) => {
		event.preventDefault();
		if (identifier.trim() && password) {
			const res = await login(identifier.trim(), password);
			if (!get(res, 'status.OK')) {
				setErrMsg(get(res, 'errorMsg'));
			}
		} else {
			setErrMsg('Das Passwort darf nicht leer sein.');
		}
	};

	const handleKeyPress = (event: React.KeyboardEvent<HTMLFormElement>) => {
		if (event.key === 'Enter') {
			handleLogin(event);
		}
	};

	return (
		<>
			{errMsg && (
				<Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={Boolean(errMsg)} autoHideDuration={2000}>
					<Alert severity='error' onClose={() => setErrMsg('')}>
						{errMsg}
					</Alert>
				</Snackbar>
			)}

			<Dialog
				open={openRegistrationDialog}
				onClose={() => setOpenRegistrationDialog(false)}
				sx={{
					textAlign: 'left',
					'& .MuiDialog-paper': {
						backgroundColor: '#eeeeee',
						maxWidth: '55rem',
						width: '35rem',
					},
				}}
			>
				<DialogTitle>Neues Benutzerkonto anlegen</DialogTitle>
				<DialogContent>
					<>
						<Stack spacing={3}>
							<Typography>
								Sie sind dabei, ein neues Benutzerkonto für das Antragsportal der Innovationsförderung Hessen zu registrieren. Hierfür stehen
								grundsätzlich zwei Optionen zur Verfügung:
							</Typography>
							<Box>
								<Typography sx={{ fontWeight: 'bold' }}>Registrierung als Organisation:</Typography>
								<Typography>
									Wenn Sie für eine Organisation (Unternehmen, Kommune, Freiberufler, Verein o.Ä.) einen Antrag auf Förderung erstellen
									möchten, klicken Sie bitte auf den Button „Registrierung Organisation“ und legen Sie Ihr Benutzerkonto an.
								</Typography>
								<DialogActions sx={{ justifyContent: 'flex-end' }}>
									<Button
										sx={{ width: 'max-content', maxWidth: 'none', whiteSpace: 'nowrap' }}
										onClick={() =>
											navigate(PAGES_URLS.Registrierung, {
												state: { registrierenAls: 'KMU' },
											})
										}
									>
										Registrierung Organisation →
									</Button>
								</DialogActions>
							</Box>
							<Divider sx={{ borderStyle: 'dashed', borderColor: 'black', borderWidth: 1 }} />
							<Box>
								<Typography sx={{ fontWeight: 'bold' }}>Registrierung als Hochschule / Forschungseinrichtung:</Typography>
								<Typography>
									Sie sind für eine Hochschule oder wissenschaftliche Einrichtung tätig und möchten eine Förderung für Ihr Forschungsprojekt
									beantragen? Dann klicken Sie bitte unten auf den Button „Registrierung Hochschule/Forschungseinrichtung“ und folgen den weiteren Anweisungen.
								</Typography>
								<DialogActions sx={{ justifyContent: 'flex-end' }}>
									<Button
										sx={{ width: 'max-content', maxWidth: 'none', whiteSpace: 'nowrap' }}
										onClick={() =>
											navigate(PAGES_URLS.AbteilungRegistrierung, {
												state: { registrierenAls: 'Abteilung' },
											})
										}
									>
										Registrierung Hochschule / Forschungseinrichtung →
									</Button>
								</DialogActions>
							</Box>
						</Stack>
					</>
				</DialogContent>
			</Dialog>

			<Box
				sx={{
					display: 'flex',
					minHeight: '100vh',
					justifyContent: 'center',
					alignItems: 'center',
					backgroundImage: `url(${backgroundImage})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
				}}
			>
				<Box
					sx={{
						maxHeight: '80%',
						display: 'grid',
						gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
						gap: 2,
						alignItems: 'center',
						padding: '2rem',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							width: { xs: '100%', sm: '60' },
							justifyContent: 'center',
							alignSelf: 'flex-start',
						}}
					>
						<Box sx={{ maxWidth: { lg: '41.188rem' } }}>
							<Box
								sx={{
									height: 'auto',
									display: 'flex',
									alignItems: 'baseline',
									gap: 2,
									backgroundColor: (theme) => alpha(theme.palette.common.white, 0.75),
									padding: '.5rem 2rem .5rem 1rem',
									width: 'fit-content',
								}}
							>
								<Box
									sx={{
										width: '1.5rem',
										height: '1.5rem',
										backgroundColor: 'navbar.secondary.active',
									}}
								/>
								<Box sx={{ width: '100%', lineHeight: 1.2, color: 'primary.main' }}>
									<Typography
										variant='h3'
										component='div'
										sx={{
											fontSize: { sm: '1.6rem', md: '2rem' },
											fontFamily: 'Avenir Next LT Pro',
											fontWeight: '600',
											wordWrap: 'break-word',
										}}
									>
										Antragsportal der Innovationsförderung Hessen
									</Typography>
								</Box>
							</Box>

							<Box
								sx={{
									backgroundColor: (theme) => alpha(theme.palette.common.white, 0.75),
									padding: '.5rem 2rem .5rem 1rem',
									width: 'fit-content',
									marginTop: '1rem',
									textAlign: 'left',
									color: 'primary.main',
								}}
							>
								<Stack spacing={2}>
									<Typography variant='body1' sx={{ fontWeight: 600 }}>
										Willkommen im digitalen Antragsportal der Innovationsförderung Hessen.
									</Typography>
									<Typography variant='body1'>
										Von der ersten Skizze bis zum Verwendungsnachweis – dieses Portal begleitet Sie durch alle Phasen Ihres Förderprojekts.
										Es unterstützt Sie zuverlässig bei der Antragstellung und Projektverwaltung und führt Sie Schritt für Schritt durch den
										gesamten Prozess.
									</Typography>
									<Typography variant='body1' sx={{ fontWeight: 600 }}>
										Wir freuen uns auf Ihre innovativen Ideen!
									</Typography>
								</Stack>
							</Box>
						</Box>
					</Box>

					<Box
						sx={{
							display: 'flex',
							width: { xs: '100%', sm: '60' },
							justifyContent: 'flex-end',
							alignItems: 'center',
						}}
					>
						<Card
							sx={{
								maxWidth: { lg: '28.125rem' },
								maxHeight: 'max-content',
								bgcolor: 'navbar.secondary.main',
								boxShadow: 3,
								p: '1rem',
							}}
						>
							<CardContent>
								<Typography sx={{ mb: '1rem', display: 'flex', justifyContent: 'space-between' }}>
									<img src={logoLinks} alt='Innovationsförderung Hessen link' />
									<img src={logoRecht} alt='Innovationsförderung Hessen' />
								</Typography>

								<Typography
									variant='body1'
									sx={{
										color: 'text.dark',
										fontFeatureSettings: "'liga' off, 'clig' off",
										fontStyle: 'normal',
										fontWeight: 400,
									}}
								>
									<strong>Haben Sie bereits einen Account?</strong> Bitte loggen Sie sich hier mit Ihren Zugangsdaten ein, um Ihre Projekte
									zu verwalten!
								</Typography>

								<form onSubmit={handleLogin} onKeyDown={handleKeyPress}>
									<TextField
										fullWidth
										id={`username-input`}
										type={'text'}
										aria-labelledby={`username-label`}
										aria-describedby='Hier geben Sie Ihr Benutzername ein.'
										autoComplete='username'
										label='Benutzername'
										value={identifier}
										onChange={(e) => setIdentifier(e.target.value)}
									/>
									<TextField
										fullWidth
										label='Passwort'
										aria-labelledby={`Passworrt-label`}
										aria-describedby='Hier Geben Sie Ihr Passwort ein.'
										type={showPassword ? 'text' : 'password'}
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										InputProps={{
											endAdornment: (
												<IconButton
													aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
													aria-pressed={showPassword}
													onClick={() => setShowPassword(!showPassword)}
												>
													{showPassword ?
														<Visibility />
													:	<VisibilityOff />}
												</IconButton>
											),
										}}
									/>
									<Button
										variant='loginButton'
										type='submit'
										sx={{
											maxWidth: '100%',
											borderRadius: '1px',
											cursor: 'pointer',
											margin: '2rem 0',
											padding: '0.75rem  1.375rem ',
										}}
									>
										Anmelden
									</Button>
								</form>

								<Typography
									variant='body1'
									sx={{
										color: 'text.dark',
										fontFeatureSettings: "'liga' off, 'clig' off",
										fontStyle: 'normal',
										fontWeight: 400,
									}}
								>
									<strong>Sie haben noch keine Zugangsdaten?</strong> Dann erstellen Sie bitte zunächst ein Benutzerkonto. Klicken Sie hierzu
									einfach auf:{' '}
									<Link
										sx={{
											color: 'navbar.secondary.active',
											textDecoration: 'none',
											'&:hover': { textDecoration: 'underline', cursor: 'pointer' },
										}}
										onClick={() => setOpenRegistrationDialog(true)}
									>
										Registrieren
									</Link>
								</Typography>

								<Box sx={{ mt: '1.5rem', textAlign: 'right' }}>
									<Link
										sx={{
											color: 'navbar.secondary.active',
											textDecoration: 'none',
											'&:hover': { textDecoration: 'underline', cursor: 'pointer' },
										}}
										// top={PAGES_URLS.PasswortVergessen}
										onClick={() => navigate(PAGES_URLS.PasswortVergessen)}
									>
										Passwort vergessen?
									</Link>
								</Box>
							</CardContent>
						</Card>
					</Box>
				</Box>
			</Box>
		</>
	);
}

export default Login;
